import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeftIcon,
  DocumentPlusIcon,
  CloudArrowUpIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { questionBankAPI } from '../../services/api'
import toast from 'react-hot-toast'

const QuestionBankAddPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { category, assessmentType } = location.state || {}
  
  const [activeTab, setActiveTab] = useState<'single' | 'bulk'>('single')
  const [loading, setLoading] = useState(false)
  
  // Single question form
  const [singleQuestion, setSingleQuestion] = useState({
    title: '',
    description: '',
    options: ['', '', '', ''], // For MCQ: 4 options
    correctAnswer: '', // For MCQ: A, B, C, or D
    testCases: [{ input: '', expected_output: '' }], // For coding: test cases
    image: null as File | null, // Image file for the question
    imagePreview: '' as string, // Base64 preview URL
    difficulty_level: 'medium' as 'easy' | 'medium' | 'hard' // Default difficulty
  })

  // Bulk upload
  const [bulkText, setBulkText] = useState('')

  // Helper functions
  const addTestCase = () => {
    setSingleQuestion({
      ...singleQuestion,
      testCases: [...singleQuestion.testCases, { input: '', expected_output: '' }]
    })
  }

  const removeTestCase = (index: number) => {
    if (singleQuestion.testCases.length > 1) {
      setSingleQuestion({
        ...singleQuestion,
        testCases: singleQuestion.testCases.filter((_, i) => i !== index)
      })
    }
  }

  const updateTestCase = (index: number, field: 'input' | 'expected_output', value: string) => {
    const newTestCases = [...singleQuestion.testCases]
    newTestCases[index][field] = value
    setSingleQuestion({ ...singleQuestion, testCases: newTestCases })
  }

  const updateOption = (index: number, value: string) => {
    const newOptions = [...singleQuestion.options]
    newOptions[index] = value
    setSingleQuestion({ ...singleQuestion, options: newOptions })
  }

  // Image upload handlers
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file')
        return
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB')
        return
      }
      
      setSingleQuestion({ ...singleQuestion, image: file })
      
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setSingleQuestion(prev => ({ ...prev, imagePreview: e.target?.result as string }))
      }
      reader.readAsDataURL(file)
    }
  }

  const removeImage = () => {
    setSingleQuestion({ ...singleQuestion, image: null, imagePreview: '' })
  }

  const handleAddSingleQuestion = async () => {
    // For MCQ questions (aptitude, reasoning, fundamentals, cloud_fundamentals, presales_fundamentals, english_communication, ai_fundamental), title is optional
    // For AI/ML Assignment, title is also optional (only description needed)
    const isMCQCategory = assessmentType === 'aptitude' || 
                          assessmentType === 'reasoning' || 
                          assessmentType === 'fundamentals' ||
                          assessmentType === 'cloud_fundamentals' ||
                          assessmentType === 'presales_fundamentals' ||
                          assessmentType === 'english_communication' ||
                          assessmentType === 'ai_fundamental' ||
                          assessmentType === 'quantitative_ability' ||
                          assessmentType === 'logical_reasoning' ||
                          assessmentType === 'verbal_ability'
    
    const isAssignmentCategory = assessmentType === 'aiml_assignment'
    
    if (!isMCQCategory && !isAssignmentCategory && !singleQuestion.title) {
      toast.error('Please fill in the title')
      return
    }
    
    if (!singleQuestion.description) {
      toast.error('Please fill in the description')
      return
    }

    setLoading(true)
    try {
      // Convert image to base64 if present
      let imageBase64 = null
      if (singleQuestion.image) {
        imageBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(singleQuestion.image!)
        })
      }

      // Map to backend format
      const questionData: any = {
        title: singleQuestion.title || undefined,  // Optional for MCQ questions
        description: singleQuestion.description,
        category: `${category.collegeType}_${category.tier}`,
        question_type: assessmentType === 'coding' ? 'coding' : 
                      assessmentType === 'case-study' ? 'case_study' :
                      assessmentType === 'business-simulations' ? 'business_simulation' :
                      assessmentType === 'aptitude' ? 'aptitude' :
                      assessmentType === 'fundamentals' ? 'fundamentals' :
                      assessmentType === 'cloud_fundamentals' ? 'cloud_fundamentals' :
                      assessmentType === 'presales_fundamentals' ? 'presales_fundamentals' :
                      assessmentType === 'english_communication' ? 'english_communication' :
                      assessmentType === 'ai_fundamental' ? 'ai_fundamental' :
                      assessmentType === 'aiml_assignment' ? 'aiml_assignment' :
                      assessmentType === 'quantitative_ability' ? 'quantitative_ability' :
                      assessmentType === 'logical_reasoning' ? 'logical_reasoning' :
                      assessmentType === 'verbal_ability' ? 'verbal_ability' : 'multiple_choice',
        difficulty_level: singleQuestion.difficulty_level, // Include selected difficulty
        marks: 10, // Default marks
        question_data: {
          problem_statement: singleQuestion.description,
          ...(imageBase64 && { image: imageBase64 })
        },
        is_verified: true,
        is_active: true
      }

      // Add type-specific data
      if (assessmentType === 'coding') {
        // For coding questions: use test cases array
        const validTestCases = singleQuestion.testCases.filter(tc => tc.input.trim() && tc.expected_output.trim())
        if (validTestCases.length === 0) {
          toast.error('Please add at least one test case')
          return
        }
        questionData.question_data.test_cases = validTestCases
      }

      if (assessmentType === 'aptitude' || 
          assessmentType === 'fundamentals' || 
          assessmentType === 'cloud_fundamentals' || 
          assessmentType === 'presales_fundamentals' || 
          assessmentType === 'english_communication' ||
          assessmentType === 'ai_fundamental' ||
          assessmentType === 'quantitative_ability' ||
          assessmentType === 'logical_reasoning' ||
          assessmentType === 'verbal_ability') {
        // For MCQ: use options array
        const validOptions = singleQuestion.options.filter(opt => opt.trim())
        if (validOptions.length < 2) {
          toast.error('Please add at least 2 options')
          return
        }
        if (!singleQuestion.correctAnswer) {
          toast.error('Please select the correct answer')
          return
        }
        
        questionData.options = validOptions.reduce((acc: any, opt, idx) => {
          acc[String.fromCharCode(65 + idx)] = opt.trim()
          return acc
        }, {})
        questionData.correct_answer = { answer: singleQuestion.correctAnswer }
      }

      if (assessmentType === 'case-study' || assessmentType === 'business-simulations') {
        // For case studies: use options as analysis questions
        const analysisQuestions = singleQuestion.options.filter(o => o.trim())
        if (analysisQuestions.length === 0) {
          toast.error('Please add at least one analysis question')
          return
        }
        questionData.question_data.analysis_questions = analysisQuestions
        questionData.question_data.expected_analysis = singleQuestion.description
      }

      if (assessmentType === 'aiml_assignment') {
        // For AI/ML Assignment: only description is needed, no options or test cases
        questionData.question_data.assignment_instructions = singleQuestion.description
      }

      await questionBankAPI.createQuestion(questionData)
      
      toast.success('Question added successfully!')
      
      // Reset form
      setSingleQuestion({
        title: '',
        description: '',
        options: ['', '', '', ''],
        correctAnswer: '',
        testCases: [{ input: '', expected_output: '' }],
        image: null,
        imagePreview: '',
        difficulty_level: 'medium' // Reset to default difficulty
      })
    } catch (error: any) {
      console.error('Error adding question:', error)
      toast.error(error.response?.data?.detail || 'Failed to add question')
    } finally {
      setLoading(false)
    }
  }

  const handleBulkUpload = async () => {
    if (!bulkText.trim()) {
      toast.error('Please paste questions in the text area')
      return
    }

    setLoading(true)
    try {
      // Parse bulk text (assuming JSON format or line-by-line format)
      const lines = bulkText.split('\n').filter(line => line.trim())
      const questions = []

      for (const line of lines) {
        try {
          // Try parsing as JSON first
          const parsed = JSON.parse(line)
          questions.push({
            ...parsed,
            category: `${category.collegeType}_${category.tier}`,
            question_type: assessmentType === 'coding' ? 'coding' : 
                      assessmentType === 'case-study' ? 'case_study' :
                      assessmentType === 'business-simulations' ? 'business_simulation' :
                      assessmentType === 'aptitude' ? 'aptitude' :
                      assessmentType === 'fundamentals' ? 'fundamentals' :
                      assessmentType === 'cloud_fundamentals' ? 'cloud_fundamentals' :
                      assessmentType === 'presales_fundamentals' ? 'presales_fundamentals' :
                      assessmentType === 'english_communication' ? 'english_communication' :
                      assessmentType === 'ai_fundamental' ? 'ai_fundamental' :
                      assessmentType === 'quantitative_ability' ? 'quantitative_ability' :
                      assessmentType === 'logical_reasoning' ? 'logical_reasoning' :
                      assessmentType === 'verbal_ability' ? 'verbal_ability' : 'multiple_choice',
            is_verified: true,
            is_active: true
          })
        } catch {
          // If not JSON, treat as simple format: Title | Description | Difficulty
          const parts = line.split('|').map(p => p.trim())
          if (parts.length >= 2) {
            questions.push({
              title: parts[0],
              description: parts[1],
              difficulty_level: parts[2] || 'medium',
              category: `${category.collegeType}_${category.tier}`,
              question_type: assessmentType === 'coding' ? 'coding' : 
                      assessmentType === 'case-study' ? 'case_study' :
                      assessmentType === 'business-simulations' ? 'business_simulation' :
                      assessmentType === 'aptitude' ? 'aptitude' :
                      assessmentType === 'fundamentals' ? 'fundamentals' :
                      assessmentType === 'cloud_fundamentals' ? 'cloud_fundamentals' :
                      assessmentType === 'presales_fundamentals' ? 'presales_fundamentals' :
                      assessmentType === 'english_communication' ? 'english_communication' :
                      assessmentType === 'ai_fundamental' ? 'ai_fundamental' :
                      assessmentType === 'quantitative_ability' ? 'quantitative_ability' :
                      assessmentType === 'logical_reasoning' ? 'logical_reasoning' :
                      assessmentType === 'verbal_ability' ? 'verbal_ability' : 'multiple_choice',
              marks: 10,
              question_data: { problem_statement: parts[1] },
              is_verified: true,
              is_active: true
            })
          }
        }
      }

      if (questions.length === 0) {
        toast.error('No valid questions found. Check format.')
        return
      }

      // Bulk create
      await questionBankAPI.bulkCreateQuestions(questions)
      
      toast.success(`Successfully added ${questions.length} questions!`)
      setBulkText('')
    } catch (error: any) {
      console.error('Error bulk uploading:', error)
      toast.error(error.response?.data?.detail || 'Failed to upload questions')
    } finally {
      setLoading(false)
    }
  }

  if (!category || !assessmentType) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Invalid navigation state</p>
          <button onClick={() => navigate('/recruiter/question-bank')} className="btn btn-primary">
            Go to Question Bank
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mr-4"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back
            </button>
          </div>
          
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Add Questions</h1>
            <p className="text-gray-600 mt-2">
              {category.collegeType === 'engineering' ? 'Engineering' : 'Management'} - 
              {category.tier === 'tier1' ? ' Tier 1' : ' Tier 2'} - {assessmentType}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="card mb-6">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('single')}
                className={`px-6 py-4 font-medium transition-colors ${
                  activeTab === 'single'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <DocumentPlusIcon className="w-5 h-5 inline mr-2" />
                Add Single Question
              </button>
              <button
                onClick={() => setActiveTab('bulk')}
                className={`px-6 py-4 font-medium transition-colors ${
                  activeTab === 'bulk'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <CloudArrowUpIcon className="w-5 h-5 inline mr-2" />
                Bulk Upload
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'single' ? (
              /* Single Question Form */
              <div className="space-y-6">
                {/* Show title field only for coding, case-study, and business-simulations */}
                {/* Hide title for MCQ-based assessments: aptitude, reasoning, fundamentals, cloud_fundamentals, presales_fundamentals, english_communication, ai_fundamental */}
                {/* Title is optional for AI/ML Assignment */}
                {assessmentType !== 'aptitude' && 
                 assessmentType !== 'reasoning' && 
                 assessmentType !== 'fundamentals' && 
                 assessmentType !== 'cloud_fundamentals' && 
                 assessmentType !== 'presales_fundamentals' && 
                 assessmentType !== 'english_communication' && 
                 assessmentType !== 'ai_fundamental' && 
                 assessmentType !== 'quantitative_ability' && 
                 assessmentType !== 'logical_reasoning' && 
                 assessmentType !== 'verbal_ability' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Question Title {assessmentType === 'aiml_assignment' ? '(Optional)' : '*'}
                    </label>
                    <input
                      type="text"
                      value={singleQuestion.title}
                      onChange={(e) => setSingleQuestion({ ...singleQuestion, title: e.target.value })}
                      className="input w-full"
                      placeholder="Enter question title"
                    />
                  </div>
                )}
                
                {/* For MCQ questions, show info that title is optional
                {(assessmentType === 'aptitude' || assessmentType === 'reasoning' || assessmentType === 'fundamentals') && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                   ℹ️ Title is optional for MCQ questions. Only the question description and options are required.
                    </p>
                  </div>
                //)} */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {assessmentType === 'aiml_assignment' ? 'Assignment Instructions *' : 'Question Description *'}
                  </label>
                  <textarea
                    value={singleQuestion.description}
                    onChange={(e) => setSingleQuestion({ ...singleQuestion, description: e.target.value })}
                    className="input w-full h-32"
                    placeholder={assessmentType === 'aiml_assignment' 
                      ? 'Enter detailed assignment instructions (e.g., Build a machine learning model to predict...)' 
                      : 'Enter question description or problem statement'}
                  />
                  {assessmentType === 'aiml_assignment' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                      <p className="text-sm text-blue-700">
                        💡 This assignment will be shown to candidates who can submit their assignment link during the test.
                      </p>
                    </div>
                  )}
                </div>

                {/* Image Upload Section - Available for all question types except coding and AI/ML assignment */}
                {assessmentType !== 'coding' && assessmentType !== 'aiml_assignment' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Question Image (Optional)
                    </label>
                    <div className="space-y-3">
                      {!singleQuestion.imagePreview ? (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                            id="question-image-upload"
                          />
                          <label
                            htmlFor="question-image-upload"
                            className="cursor-pointer flex flex-col items-center"
                          >
                            <CloudArrowUpIcon className="w-8 h-8 text-gray-400 mb-2" />
                            <span className="text-sm text-gray-600">
                              Click to upload an image
                            </span>
                            <span className="text-xs text-gray-500 mt-1">
                              PNG, JPG, GIF up to 5MB
                            </span>
                          </label>
                        </div>
                      ) : (
                        <div className="relative">
                          <img
                            src={singleQuestion.imagePreview}
                            alt="Question preview"
                            className="max-w-full h-auto max-h-64 rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={removeImage}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">File:</span> {singleQuestion.image?.name}
                            <span className="ml-2 text-gray-500">
                              ({(singleQuestion.image?.size! / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Perfect for reasoning questions with figures, diagrams, charts, or visual content
                    </p>
                  </div>
                )}

                {/* Difficulty Level Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulty Level *
                  </label>
                  <div className="flex space-x-4">
                    {[
                      { value: 'easy', label: 'Easy', color: 'bg-green-100 text-green-800 border-green-200' },
                      { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
                      { value: 'hard', label: 'Hard', color: 'bg-red-100 text-red-800 border-red-200' }
                    ].map((option) => (
                      <label key={option.value} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="difficulty"
                          value={option.value}
                          checked={singleQuestion.difficulty_level === option.value}
                          onChange={(e) => setSingleQuestion({ ...singleQuestion, difficulty_level: e.target.value as 'easy' | 'medium' | 'hard' })}
                          className="sr-only"
                        />
                        <span className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          singleQuestion.difficulty_level === option.value
                            ? option.color + ' border-opacity-100'
                            : 'bg-gray-100 text-gray-600 border-gray-200 border-opacity-50'
                        }`}>
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Select the appropriate difficulty level for this question
                  </p>
                </div>

                {/* Dynamic form based on question type */}
                {assessmentType === 'coding' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">
                        Test Cases
                      </label>
                      <button
                        type="button"
                        onClick={addTestCase}
                        className="btn btn-sm btn-outline flex items-center"
                      >
                        <PlusIcon className="w-4 h-4 mr-1" />
                        Add Test Case
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      {singleQuestion.testCases.map((testCase, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-600">Test Case {index + 1}</span>
                            {singleQuestion.testCases.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeTestCase(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Input</label>
                              <input
                                type="text"
                                value={testCase.input}
                                onChange={(e) => updateTestCase(index, 'input', e.target.value)}
                                className="input w-full text-sm"
                                placeholder="[1,2,3,4,5]"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Expected Output</label>
                              <input
                                type="text"
                                value={testCase.expected_output}
                                onChange={(e) => updateTestCase(index, 'expected_output', e.target.value)}
                                className="input w-full text-sm"
                                placeholder="15"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(assessmentType === 'aptitude' || 
                  assessmentType === 'fundamentals' || 
                  assessmentType === 'cloud_fundamentals' || 
                  assessmentType === 'presales_fundamentals' || 
                  assessmentType === 'english_communication' ||
                  assessmentType === 'ai_fundamental' ||
                  assessmentType === 'quantitative_ability' ||
                  assessmentType === 'logical_reasoning' ||
                  assessmentType === 'verbal_ability') && (
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Multiple Choice Options
                    </label>
                    
                    <div className="space-y-3">
                      {singleQuestion.options.map((option, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                            {String.fromCharCode(65 + index)}
                          </div>
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => updateOption(index, e.target.value)}
                            className="input flex-1"
                            placeholder={`Option ${String.fromCharCode(65 + index)}`}
                          />
                        </div>
                      ))}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Correct Answer
                      </label>
                      <select
                        value={singleQuestion.correctAnswer}
                        onChange={(e) => setSingleQuestion({ ...singleQuestion, correctAnswer: e.target.value })}
                        className="input w-full"
                      >
                        <option value="">Select correct answer</option>
                        {singleQuestion.options.map((_, index) => (
                          <option key={index} value={String.fromCharCode(65 + index)}>
                            {String.fromCharCode(65 + index)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {(assessmentType === 'case-study' || assessmentType === 'business-simulations') && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Analysis Questions
                      </label>
                      <div className="space-y-3">
                        {singleQuestion.options.map((question, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-medium mt-1">
                              {index + 1}
                            </div>
                            <textarea
                              value={question}
                              onChange={(e) => updateOption(index, e.target.value)}
                              className="input flex-1 h-20"
                              placeholder={`Analysis question ${index + 1}...`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleAddSingleQuestion}
                  className="btn btn-primary w-full"
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add Question'}
                </button>
              </div>
            ) : (
              /* Bulk Upload */
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-900 mb-2">Format Instructions:</h4>
                  <p className="text-sm text-blue-700 mb-2">Paste questions in one of these formats:</p>
                  <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li><strong>Simple:</strong> Title | Description | Difficulty (one per line)</li>
                    <li><strong>JSON:</strong> One JSON object per line with all fields</li>
                  </ul>
                  <p className="text-xs text-blue-600 mt-2">
                    Example: Two Sum Problem | Find two numbers that add up to target | medium
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Paste Questions (one per line)
                  </label>
                  <textarea
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    className="input w-full h-96 font-mono text-sm"
                    placeholder="Title 1 | Description 1 | medium&#10;Title 2 | Description 2 | hard&#10;..."
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Lines processed: {bulkText.split('\n').filter(l => l.trim()).length}
                  </p>
                </div>

                <button
                  onClick={handleBulkUpload}
                  className="btn btn-primary w-full"
                  disabled={loading}
                >
                  {loading ? 'Uploading...' : 'Upload Questions'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Success Info */}
        <div className="card p-6 bg-green-50">
          <p className="text-sm text-green-700">
            ✓ Questions will be automatically available for randomized selection in drives
          </p>
        </div>
      </div>
    </div>
  )
}

export default QuestionBankAddPage

